/**
 * Local (device) notifications — the ONLY module in the app that imports
 * `@capacitor/local-notifications`.
 *
 * Per the Capacitor conventions (.github/instructions/capacitor-plugins.instructions.md),
 * components and stores never touch a plugin directly: they go through a
 * service in `src/services/device/`. This is that service, and it exists for
 * exactly one caller — the event reminder engine (`event-reminders.ts`), which
 * uses it to put a reminder on the lock screen when Syntry is closed.
 *
 * Two properties every method here shares, deliberately:
 *
 *  - **It early-returns when unsupported.** On the web, and in any native shell
 *    where the plugin was not installed, {@link isSupported} is false and every
 *    call becomes a no-op. Callers never have to branch on the platform.
 *  - **It never throws.** Every plugin call is wrapped. A notification is a
 *    nicety; a permission dialog the OS declined to show, a vendor ROM that
 *    rejects exact alarms, a channel that already exists — none of those may
 *    take down the screen that asked. Failures are logged in dev only.
 *
 * Scope note: this is the LOCAL notification channel only. Host push (FCM /
 * APNs / Web Push registration) lives in `push.service.ts`; the OS display
 * permission, however, is read and requested HERE for both — the push plugin's
 * `checkPermissions()` always answers `granted` on Android ≤ 12. A reminder
 * reaches the user as an ERPat bell notification (server cron), an in-app
 * banner (the reminder engine's tick), and — on Android — a scheduled device
 * notification from here.
 */
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { tenantStamp } from '@/services/session-scope';
import { IS_DEV } from '@/utils/env';

/**
 * Android notification channel. Created once before the first schedule; the OS
 * ignores a repeat create for an existing id, so `ensureChannel()` is cheap.
 */
const CHANNEL_ID = 'syntry-events';

/**
 * Stamped into every notification's `extra` so {@link cancelAllMine} can tell
 * OUR pending notifications from anyone else's. Blanket-cancelling would be
 * wrong the moment a second feature starts scheduling.
 */
const SOURCE_TAG = 'syntry-event';

/** Permission states this service reports, flattened for callers. */
export type LocalNotificationPermission = 'granted' | 'denied' | 'prompt' | 'unsupported';

/** One reminder to put on the device. */
export interface LocalNotificationItem {
  /** Stable 32-bit id — see `nativeIdOf()` in event-reminders-plan.ts. */
  id: number;
  title: string;
  body: string;
  /** When the OS should show it. */
  at: Date;
  /** Deep-link payload, read back by {@link onTap}. */
  eventId: string;
  occurrenceDate: string;
}

/** What a tapped notification hands back to the app. */
export interface LocalNotificationTap {
  eventId: string;
  occurrenceDate: string;
  /**
   * The tenant the reminder was scheduled under (`tenantStamp()` at schedule
   * time — phase6-contract.md §3.7); '' for a reminder scheduled before the
   * stamp existed. A handler compares it with the current stamp before routing.
   */
  tenant: string;
}

function warn(scope: string, err: unknown): void {
  if (IS_DEV) console.warn(`[local-notifications] ${scope}`, err);
}

/**
 * Whether scheduling is possible at all.
 *
 * Web is excluded outright: the plugin's web implementation goes through the
 * browser Notification API, which cannot fire while the page is closed — the
 * only case this channel exists for. On the web the in-app banner and the
 * ERPat bell already cover the user, so a half-working web path would only
 * duplicate them while the tab is open and promise delivery it cannot make.
 */
export function isSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('LocalNotifications');
}

/** Collapse Capacitor's four-state permission into the three we act on. */
function toPermission(state: string): LocalNotificationPermission {
  if (state === 'granted') return 'granted';
  if (state === 'denied') return 'denied';
  // 'prompt' and 'prompt-with-rationale' both mean "we may still ask".
  return 'prompt';
}

/** Current permission, without prompting. */
export async function checkPermission(): Promise<LocalNotificationPermission> {
  if (!isSupported()) return 'unsupported';
  try {
    const status = await LocalNotifications.checkPermissions();
    return toPermission(status.display);
  } catch (err) {
    warn('checkPermissions', err);
    return 'unsupported';
  }
}

/**
 * Ask for permission, then — on Android 12+ — offer the exact-alarm screen.
 *
 * The second half matters for a calendar app: without `SCHEDULE_EXACT_ALARM`
 * the OS is free to batch a "20 minutes before" reminder into whatever
 * maintenance window it likes, which can be well after the meeting started.
 * Both methods are feature-detected because older native shells (and the iOS
 * implementation) do not expose them, and the call is best-effort: a user who
 * declines the system screen still gets an inexact reminder, which is better
 * than none.
 *
 * The caller is expected to have shown a rationale first — this is never
 * invoked cold from app boot.
 */
export async function requestPermission(): Promise<LocalNotificationPermission> {
  if (!isSupported()) return 'unsupported';

  let permission: LocalNotificationPermission;
  try {
    const status = await LocalNotifications.requestPermissions();
    permission = toPermission(status.display);
  } catch (err) {
    warn('requestPermissions', err);
    return 'unsupported';
  }

  if (permission !== 'granted') return permission;

  try {
    if (
      typeof LocalNotifications.checkExactNotificationSetting === 'function' &&
      typeof LocalNotifications.changeExactNotificationSetting === 'function'
    ) {
      const exact = await LocalNotifications.checkExactNotificationSetting();
      if (exact.exact_alarm !== 'granted') {
        // Opens the system settings screen; the user may simply come back
        // without granting, which we accept.
        await LocalNotifications.changeExactNotificationSetting();
      }
    }
  } catch (err) {
    // iOS, an older plugin build, or a ROM without the settings activity.
    warn('exactNotificationSetting', err);
  }

  return permission;
}

/** One Android notification channel (ignored on iOS). */
export interface LocalNotificationChannel {
  id: string;
  name: string;
  description: string;
}

/** Create an Android channel if it does not exist yet. No-op elsewhere. */
export async function ensureChannelFor(channel: LocalNotificationChannel): Promise<void> {
  if (!isSupported()) return;
  try {
    await LocalNotifications.createChannel({
      id: channel.id,
      name: channel.name,
      description: channel.description,
      importance: 4,
      visibility: 1,
    });
  } catch (err) {
    // iOS has no channels; the plugin rejects there, which is expected.
    warn('createChannel', err);
  }
}

/** Create the event-reminder channel if it does not exist yet. No-op elsewhere. */
export function ensureChannel(): Promise<void> {
  return ensureChannelFor({
    id: CHANNEL_ID,
    name: 'Event reminders',
    description: 'Reminders for events you are attending.',
  });
}

/**
 * One notification to show RIGHT NOW (no schedule) — the realtime alert path.
 * `extra.source` MUST be a tag other than the reminder engine's so
 * {@link cancelAllMine} and {@link onTap} keep ignoring it, and so
 * {@link onTapFor} can route it to its own handler.
 */
export interface LocalNotificationNow {
  /** Stable 32-bit id; re-showing an id replaces rather than duplicates. */
  id: number;
  title: string;
  body: string;
  channelId: string;
  extra: Readonly<Record<string, string>>;
}

/**
 * Show a notification immediately. Omitting `schedule` is the plugin's
 * "deliver now" form. Same never-throws contract as {@link schedule}.
 */
export async function showNow(item: LocalNotificationNow): Promise<void> {
  if (!isSupported()) return;
  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: item.id,
          title: item.title,
          body: item.body,
          channelId: item.channelId,
          extra: { ...item.extra },
        },
      ],
    });
  } catch (err) {
    warn('showNow', err);
  }
}

/**
 * Listen for a tap on a notification stamped with `extra.source === source`.
 * The generic sibling of {@link onTap}: it hands back the whole `extra` map so
 * a feature can carry its own payload keys.
 *
 * @returns a detach function. Always safe to call.
 */
export async function onTapFor(
  source: string,
  handler: (extra: Readonly<Record<string, unknown>>) => void,
): Promise<() => void> {
  if (!isSupported()) return () => {};
  try {
    const listener = await LocalNotifications.addListener(
      'localNotificationActionPerformed',
      (action) => {
        const extra = action.notification.extra as Record<string, unknown> | null | undefined;
        if (!extra || extra.source !== source) return;
        handler(extra);
      },
    );
    return () => {
      void listener.remove().catch(() => undefined);
    };
  } catch (err) {
    warn('addListener', err);
    return () => {};
  }
}

/**
 * Schedule (or re-schedule) reminders.
 *
 * Ids are deterministic, so re-scheduling an id the OS already holds replaces
 * it rather than duplicating — which is what makes the engine's "write the plan,
 * then reconcile native" pass idempotent.
 *
 * `allowWhileIdle` lets a reminder fire through Doze; without it a phone left on
 * a desk overnight delivers the morning reminder whenever it next wakes.
 *
 * Every reminder is stamped with the tenant it was scheduled under
 * (`extra.tenant`, the one normaliser in `session-scope.ts`), so a tap that
 * arrives after a company switch can be told apart from the current tenant's.
 */
export async function schedule(items: readonly LocalNotificationItem[]): Promise<void> {
  if (!isSupported() || items.length === 0) return;
  const tenant = tenantStamp();
  try {
    await LocalNotifications.schedule({
      notifications: items.map((item) => ({
        id: item.id,
        title: item.title,
        body: item.body,
        channelId: CHANNEL_ID,
        schedule: { at: item.at, allowWhileIdle: true },
        extra: {
          source: SOURCE_TAG,
          eventId: item.eventId,
          occurrenceDate: item.occurrenceDate,
          tenant,
        },
      })),
    });
  } catch (err) {
    warn('schedule', err);
  }
}

/** Cancel specific notifications by id. Unknown ids are ignored by the OS. */
export async function cancel(ids: readonly number[]): Promise<void> {
  if (!isSupported() || ids.length === 0) return;
  try {
    await LocalNotifications.cancel({ notifications: ids.map((id) => ({ id })) });
  } catch (err) {
    warn('cancel', err);
  }
}

/**
 * Cancel every pending notification THIS service scheduled.
 *
 * Filtered by our `extra.source` tag rather than cancelling everything pending:
 * the plugin is app-wide, so a blanket cancel would silently delete another
 * feature's notifications the day one is added. Used at sign-out, where leaving
 * a reminder armed would pop an event title on the lock screen of a device
 * whose session has ended.
 */
export async function cancelAllMine(): Promise<void> {
  if (!isSupported()) return;
  try {
    const pending = await LocalNotifications.getPending();
    const ids = pending.notifications
      .filter((item) => {
        const extra = item.extra as { source?: unknown } | null | undefined;
        return Boolean(extra) && extra?.source === SOURCE_TAG;
      })
      .map((item) => item.id);
    if (ids.length === 0) return;
    await LocalNotifications.cancel({ notifications: ids.map((id) => ({ id })) });
  } catch (err) {
    warn('cancelAllMine', err);
  }
}

/**
 * Listen for a tap on one of our notifications.
 *
 * @returns a detach function. Always safe to call, even when the listener was
 *   never attached (unsupported platform, or the plugin rejected).
 */
export async function onTap(
  handler: (tap: LocalNotificationTap) => void,
): Promise<() => void> {
  if (!isSupported()) return () => {};
  try {
    const listener = await LocalNotifications.addListener(
      'localNotificationActionPerformed',
      (action) => {
        const extra = action.notification.extra as
          | { source?: unknown; eventId?: unknown; occurrenceDate?: unknown; tenant?: unknown }
          | null
          | undefined;
        // Someone else's notification — not ours to route.
        if (!extra || extra.source !== SOURCE_TAG) return;
        const eventId = typeof extra.eventId === 'string' ? extra.eventId : '';
        if (!eventId) return;
        handler({
          eventId,
          occurrenceDate:
            typeof extra.occurrenceDate === 'string' ? extra.occurrenceDate : '',
          tenant: typeof extra.tenant === 'string' ? extra.tenant.trim().toLowerCase() : '',
        });
      },
    );
    return () => {
      void listener.remove().catch(() => undefined);
    };
  } catch (err) {
    warn('addListener', err);
    return () => {};
  }
}
